export { currentActorId } from '@application/common/hooks/useActorBoundary';
const draftPrefix = 'qportal.hour-draft.v1:';
const ttl = 2 * 60 * 60 * 1000;
export function draftStorageKey(actor, context) {
    return actor ? `${draftPrefix}${encodeURIComponent(actor)}:${context}` : '';
}
export function readHourDraft(key) {
    if (!key)
        return;
    try {
        const raw = sessionStorage.getItem(key);
        if (!raw)
            return;
        const value = JSON.parse(raw);
        if (typeof value.savedAt !== 'number' || Date.now() - value.savedAt > ttl || value.savedAt > Date.now() ||
            typeof value.weekVersion !== 'string' || !Array.isArray(value.rows) || value.rows.length > 200 ||
            !value.rows.every((r) => typeof r.id === 'string' && typeof r.date === 'string' &&
                typeof r.hours === 'string' && typeof r.assignmentReasonId === 'string' && typeof r.isPersisted === 'boolean')) {
            sessionStorage.removeItem(key);
            return;
        }
        return { rows: value.rows, weekVersion: value.weekVersion };
    }
    catch {
        return;
    }
}
export function saveHourDraft(key, draft) {
    if (!key)
        return false;
    try {
        // Deliberately whitelist editable primitives. Never persist user objects, tokens or files.
        const rows = draft.rows.map(({ id, date, assignmentReasonId, hours, isPersisted }) => ({ id, date, assignmentReasonId, hours, isPersisted }));
        sessionStorage.setItem(key, JSON.stringify({ rows, weekVersion: draft.weekVersion, savedAt: Date.now() }));
        return true;
    }
    catch {
        return false;
    }
}
export function clearHourDraft(key, saved) {
    try {
        // A late response from an unmounted editor must not erase a newer draft.
        if (saved && JSON.stringify(readHourDraft(key)) !== JSON.stringify(saved))
            return;
        sessionStorage.removeItem(key);
    }
    catch { /* Storage can be disabled. */ }
}
export function newHourId() {
    if (typeof crypto.randomUUID === 'function')
        return crypto.randomUUID();
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 15) | 64;
    bytes[8] = (bytes[8] & 63) | 128;
    const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
export function hourWriteError(error) {
    const payload = error?.response?.data || error;
    const status = error?.response?.status || payload?.status;
    if (payload?.code === 'HOUR_WRITE_OUTCOME_UNCERTAIN' || !status || status >= 500) {
        return { message: 'The save result is uncertain. Your input is kept. Close and reload the saved hours before trying again.', mustReload: true };
    }
    if (status === 409 || String(payload?.code || '').startsWith('HOUR_')) {
        return { message: 'These hours have changed or need to be refreshed. Your input is kept. Close and reload, then review the saved hours.', mustReload: true };
    }
    if (status === 401)
        return { message: 'Your session has expired. Sign in again and return to this week to restore your input.', mustReload: true };
    if (status === 403)
        return { message: 'You do not have permission to change these hours. Your input is kept.', mustReload: true };
    return { message: 'The hours could not be saved. Check your input and try again.', mustReload: false };
}
